import React, { ReactNode, createContext, useEffect, useRef } from "react";
import { useMapBox } from "../components/BalloonEventsMapBox";
import {Geolocation, LngLatGeolocation} from "ts-core";
import { useBalloonEventsMetadata } from "react-ui";
import { useBalloonEventsData } from "./EventData";

interface ClientEmbedAPIInfo {
  api: any;
  trigger: (event: string, data?: any) => void;
}

const ClientEmbedAPIContext = createContext<ClientEmbedAPIInfo | undefined>(
  undefined
);


export function ClientEmbedAPIProvider({ children, api, trigger }: { children: ReactNode, api: any, trigger: (event: string, data?: any) => void }) {
  const mapBoxInfo = useMapBox();
  const map = mapBoxInfo?.map;
  const metadata = useBalloonEventsMetadata();
  const { events, loading } = useBalloonEventsData();
  
  // Track previous values to detect changes
  const prevZoomRef = useRef<number | null>(null);
  const prevCenterRef = useRef<LngLatGeolocation | null>(null);

  useEffect(() => {
    if (!map) return;
    
    api["center"] = () => (map.getCenter() as LngLatGeolocation) || null;
    api["zoom"] = () => map.getZoom() || null;
    api["metadata"] = () => metadata;
    
    // Initialize previous values
    prevZoomRef.current = map.getZoom();
    prevCenterRef.current = map.getCenter() as LngLatGeolocation;
    
    const moveend = () => {
      const currentZoom = map.getZoom();
      const currentCenter = map.getCenter() as LngLatGeolocation;
      
      // Check if zoom changed
      if (prevZoomRef.current !== null && prevZoomRef.current !== currentZoom) {
        trigger("zoomChange", currentZoom);
      }
      
      // Check if location changed
      if (prevCenterRef.current !== null && 
          (prevCenterRef.current.lat !== currentCenter.lat || 
           prevCenterRef.current.lng !== currentCenter.lng)) {
        trigger("locationChange", currentCenter);
      }
      
      // Update previous values
      prevZoomRef.current = currentZoom;
      prevCenterRef.current = currentCenter;
      
      // Also trigger legacy moveend event for backward compatibility
      trigger("moveend");
    };
    
    map.on("moveend", moveend);
    trigger("ready");
    
    return () => {
      map.off("moveend", moveend);
    }
  }, [map]);

  useEffect(() => {
    if (!map) return;
    api["events"] = () => events;
    if (!loading) trigger("events", events);
  }, [events, loading]);


  return (
    <ClientEmbedAPIContext.Provider value={{ api, trigger }}>
      {children}
    </ClientEmbedAPIContext.Provider>
  );
}


export function useClientEmbedAPI(): ClientEmbedAPIInfo {
  const context = React.useContext(ClientEmbedAPIContext);
  if (!context) {
    throw new Error(
      "useClientEmbedAPI must be used within a ClientEmbedAPIProvider"
    );
  }
  return context;
}
